/**
 * Reusable JSON-LD builders for city landing pages and the home page.
 *
 * Returns an array of `<script type="application/ld+json">` objects that can be
 * dropped into a TanStack Route's `head().scripts`.
 *
 * Includes:
 *  - LocalBusiness (city-scoped, references the root #business node)
 *  - Service (city-scoped, with OfferCatalog of common services)
 *  - FAQPage (3 city-targeted Q&As)
 *  - BreadcrumbList
 */

import { absUrl, canonicalUrl, SITE_URL, SITE_NAME } from "@/lib/seo";
import {
  GEO_COORDINATES,
  HAS_MAP,
  OPENING_HOURS,
  SAME_AS,
  BUSINESS,
} from "@/lib/businessJsonLd";

import heroImage from "@/assets/original/hero-shade.jpg";

const DEFAULT_IMAGE = absUrl(heroImage);

const SERVICES = [
  { name: "Custom Awnings", path: "/awnings" },
  { name: "Commercial Awnings", path: "/commercial-awnings" },
  { name: "Sail Shades", path: "/sail-shades" },
  { name: "Patio & Gazebo-Pergola Covers", path: "/pergola-covers" },
  { name: "Boat Covers & Marine Canvas", path: "/boat-covers" },
  { name: "Stock Trailer Covers", path: "/stock-trailer-covers" },
  { name: "Outdoor Curtains & Panels", path: "/curtains" },
  { name: "Welding & Custom Fabrication", path: "/custom-fabrication" },
];

function cityServices(city: string) {
  return SERVICES.map((s) => ({
    "@type": "Service",
    name: `${s.name} — ${city}, TX`,
    url: canonicalUrl(s.path),
  }));
}


function cityFaqs(city: string, region: string) {
  return [
    {
      q: `Do you install awnings, sail shades and covers in ${city}, TX?`,
      a: `Yes — we serve ${city} and the surrounding ${region} area. We design, fabricate and install custom welded aluminum awnings, sail shades, patio and pergola covers, marine canvas and stock trailer covers throughout the region.`,
    },
    {
      q: `How long does a custom awning or shade structure last in ${city}?`,
      a: `Our welded aluminum frames are built to last 20+ years. Sunbrella® and Tempotest® canvas tops typically hold their color and integrity for 8–12 years in direct Texas sun before needing a re-cover.`,
    },
    {
      q: `How do I get a free quote for a project in ${city}?`,
      a: `Send a few photos and rough measurements through the form on our site or call +1 (830) 390-9588. We respond same business day with sizing questions and a free, no-pressure estimate. On-site measurements are available across our service area.`,
    },
  ];
}

export function cityHeadScripts(
  city: string,
  region: string,
  options?: {
    /** Neighborhoods / sub-areas inside the city, added to areaServed. */
    areaServedPlaces?: string[];
  },
) {
  const path = pathFromCity(city);
  const url = canonicalUrl(path);

  const cityPlace = {
    "@type": "City",
    name: city,
    containedInPlace: { "@type": "AdministrativeArea", name: `${region}, Texas` },
  };
  const areaServed =
    options?.areaServedPlaces && options.areaServedPlaces.length > 0
      ? [
          cityPlace,
          ...options.areaServedPlaces.map((n) => ({
            "@type": "Place",
            name: `${n}, ${city}, TX`,
          })),
        ]
      : cityPlace;

  const localBusiness = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
    "@id": `${url}#business`,
    name: `${SITE_NAME} — ${city}`,
    parentOrganization: { "@id": `${SITE_URL}/#business` },
    url,
    telephone: BUSINESS.telephone,
    email: BUSINESS.email,
    image: DEFAULT_IMAGE,
    priceRange: BUSINESS.priceRange,
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.street,
      addressLocality: BUSINESS.city,
      addressRegion: BUSINESS.region,
      postalCode: BUSINESS.postalCode,
      addressCountry: BUSINESS.country,
    },
    // GBP-aligned signals: same coordinates, map link, hours and profile
    // links the Google Business Profile carries, so the listing and the
    // site describe one identical business.
    geo: GEO_COORDINATES,
    hasMap: HAS_MAP,
    openingHoursSpecification: OPENING_HOURS,
    sameAs: SAME_AS,
    currenciesAccepted: "USD",
    paymentAccepted: "Cash, Check, Credit Card",
    foundingDate: BUSINESS.foundingDate,
    areaServed,
    description: `Custom welded awnings, sail shades, patio & pergola covers, marine canvas and aluminum fabrication serving ${city}, TX and the surrounding ${region} area.`,
  };


  const service = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    serviceType: "Custom Awnings, Covers & Shade Structures",
    provider: { "@id": `${SITE_URL}/#business` },
    areaServed,
    name: `Custom Awnings, Covers & Shade Structures in ${city}, TX`,
    url,
    knowsAbout: cityServices(city),
  };

  const faqs = cityFaqs(city, region);
  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${url}#faq`,
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: `${city}, TX`, item: url },
    ],
  };

  return [
    { type: "application/ld+json", children: JSON.stringify(localBusiness) },
    { type: "application/ld+json", children: JSON.stringify(service) },
    { type: "application/ld+json", children: JSON.stringify(faq) },
    { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
  ];
}

/** Best-effort city -> route path; matches the file slug pattern used in src/routes. */
function pathFromCity(city: string): string {
  const slug = city
    .toLowerCase()
    .replace(/['']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `/${slug}`;
}

const HOME_FAQS = [
  {
    q: "What areas does Texas Made Shade serve?",
    a: "We're based in Seguin, TX and install custom awnings, sail shades, patio & pergola covers, marine canvas and stock trailer covers across Central Texas, the Hill Country, San Antonio metro, Austin metro, South Texas, the Rio Grande Valley and the Texas Gulf Coast.",
  },
  {
    q: "Do you build commercial awnings for storefronts and restaurants?",
    a: "Yes. Storefront, restaurant and commercial entry awnings are a core part of our business. We pattern on-site, fabricate the welded aluminum frame in our Seguin shop, sew the canvas in-house and install with our own crew.",
  },
  {
    q: "What fabric do you use for awnings, sail shades and boat covers?",
    a: "Sunbrella® and Tempotest® premium canvas for awnings, Phifertex® Plus mesh for sail shades, and Sunbrella® Marine for boat covers — all UV-stable, mildew-resistant, and built for Texas sun and Gulf humidity.",
  },
  {
    q: "How long do custom awnings last in Texas?",
    a: "Our welded aluminum frames last 20+ years. Premium canvas tops typically hold up 8–12 years in direct Texas sun before needing a re-cover, depending on exposure.",
  },
  {
    q: "How do I get a free quote?",
    a: "Send measurements, photos and project notes through the form on our homepage, or call +1 (830) 390-9588. On-site measurements are free across our service area.",
  },
];

export function homeHeadScripts() {
  const url = `${SITE_URL}/`;

  const service = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}/#service`,
    serviceType: "Custom Awnings, Covers & Shade Structures",
    provider: { "@id": `${SITE_URL}/#business` },
    name: "Custom Awnings, Covers, Sail Shades & Marine Canvas — Texas Made Shade",
    url,
    areaServed: [
      { "@type": "State", name: "Texas" },
      { "@type": "City", name: "San Antonio" },
      { "@type": "City", name: "Austin" },
      { "@type": "City", name: "New Braunfels" },
      { "@type": "City", name: "Corpus Christi" },
      { "@type": "City", name: "Houston" },
    ],
    knowsAbout: cityServices("Texas"),
  };

  const faq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${SITE_URL}/#faq`,
    mainEntity: HOME_FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return [
    { type: "application/ld+json", children: JSON.stringify(service) },
    { type: "application/ld+json", children: JSON.stringify(faq) },
  ];
}

/**
 * Local schema for a service × city page (e.g. `/awnings/in/boerne`).
 *
 * Emits a city-scoped LocalBusiness node (NAP + geo + hours + map link that
 * match the Google Business Profile) and a service-level BreadcrumbList, so
 * every city-targeted service URL carries the same local signals the city
 * hub pages do.
 */
export function serviceCityHeadScripts(input: {
  /** Canonical path, e.g. `/awnings/in/boerne`. */
  path: string;
  /** Human service name, e.g. "Custom Awnings". */
  serviceName: string;
  /** City name, e.g. "Boerne". */
  city: string;
  /** County / region label, e.g. "Kendall County". */
  region: string;
  /** Parent service path for the breadcrumb, e.g. `/awnings`. */
  parentPath: string;
  /** Parent service label for the breadcrumb, e.g. "Awnings". */
  parentName: string;
}) {
  const url = canonicalUrl(input.path);

  const localBusiness = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
    "@id": `${url}#business`,
    name: `${SITE_NAME} — ${input.serviceName} in ${input.city}, TX`,
    parentOrganization: { "@id": `${SITE_URL}/#business` },
    url,
    telephone: BUSINESS.telephone,
    email: BUSINESS.email,
    image: DEFAULT_IMAGE,
    priceRange: BUSINESS.priceRange,
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.street,
      addressLocality: BUSINESS.city,
      addressRegion: BUSINESS.region,
      postalCode: BUSINESS.postalCode,
      addressCountry: BUSINESS.country,
    },
    geo: GEO_COORDINATES,
    hasMap: HAS_MAP,
    openingHoursSpecification: OPENING_HOURS,
    sameAs: SAME_AS,
    currenciesAccepted: "USD",
    paymentAccepted: "Cash, Check, Credit Card",
    areaServed: {
      "@type": "City",
      name: input.city,
      containedInPlace: { "@type": "AdministrativeArea", name: `${input.region}, Texas` },
    },
    description: `${input.serviceName} designed, fabricated and installed in ${input.city}, TX and the surrounding ${input.region} area by ${SITE_NAME} of Seguin.`,
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: input.parentName, item: canonicalUrl(input.parentPath) },
      { "@type": "ListItem", position: 3, name: `${input.parentName} in ${input.city}, TX`, item: url },
    ],
  };

  return [
    { type: "application/ld+json", children: JSON.stringify(localBusiness) },
    { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
  ];
}
